import type { OpsLogisticDefinition, OpsStorageRef } from '../types';
import { OPS_LOGISTICS_CATALOG } from './logisticsCatalog';

const LOCATION_FIELD_PATHS = new Set([
  'address',
  'arrivalDismissalProcedure',
  'arrivalProcedure',
  'attendanceSoftware',
  'bathroomRules',
  'campusPhoto',
  'dismissalProcedure',
  'emergencyContactOnSite',
  'firstDayInstructionsSent',
  'hallwayRules',
  'lateArrivalProcedure',
  'parkingInstructions',
  'pocContactId',
  'pocContactInfo',
  'pocOnSite',
  'registrationLink',
  'sitesWhitelisted',
  'wifiPassword',
  'wifiRules',
  'wifiUsername',
]);

const ID_ALIASES: Readonly<Record<string, string>> = Object.freeze({
  'school.name': 'partner.name',
  'school.additionalNotes': 'squad.additionalNotes',
  'school.gradeLevelsOffered': 'partner.demographics.gradeLevelsOffered',
  'school.logo': 'partner.branding.logoUrl',
  'school.reliabilityScore': 'partner.demographics.reliabilityScore',
});

function canonicalFieldPath(legacyFieldPath: string, locationScoped: boolean): string {
  if (locationScoped && legacyFieldPath === 'campusPhoto') return 'campusPhotoUrls';
  if (locationScoped && legacyFieldPath === 'pocContactInfo') return 'pocContact';
  if (legacyFieldPath.startsWith('billingProfile.')) {
    return `billingDefaults.${legacyFieldPath.slice('billingProfile.'.length)}`;
  }
  if (legacyFieldPath === 'gradeLevelsOffered') return 'demographics.gradeLevelsOffered';
  if (legacyFieldPath === 'logo') return 'branding.logoUrl';
  if (legacyFieldPath === 'reliabilityScore') return 'demographics.reliabilityScore';
  return legacyFieldPath;
}

export function canonicalLogisticIdForLegacySchool(logisticId: string): string | null {
  const id = String(logisticId || '').trim();
  if (!id.startsWith('school.')) return null;
  if (ID_ALIASES[id]) return ID_ALIASES[id];
  const fieldPath = id.slice('school.'.length);
  return `${LOCATION_FIELD_PATHS.has(fieldPath) ? 'location' : 'partner'}.${fieldPath}`;
}

function canonicalStorageForLegacySchool(definition: OpsLogisticDefinition): OpsStorageRef {
  const legacyFieldPath = definition.storage.kind === 'billing_profile'
    || definition.storage.kind === 'schools'
    ? definition.storage.fieldPath
    : definition.id.slice('school.'.length);
  if (definition.storage.kind === 'billing_profile') {
    return { kind: 'partners', fieldPath: `billingDefaults.${legacyFieldPath}` };
  }
  if (legacyFieldPath === 'additionalNotes') {
    return { kind: 'squads', fieldPath: 'additionalNotes' };
  }
  const locationScoped = LOCATION_FIELD_PATHS.has(legacyFieldPath);
  return {
    kind: locationScoped ? 'locations' : 'partners',
    fieldPath: canonicalFieldPath(legacyFieldPath, locationScoped),
  };
}

export const TRANSLATED_LEGACY_SCHOOL_LOGISTICS_CATALOG: ReadonlyArray<OpsLogisticDefinition> =
  Object.freeze(
    OPS_LOGISTICS_CATALOG
      .filter((definition) => definition.id.startsWith('school.'))
      .map((definition) => {
        const id = canonicalLogisticIdForLegacySchool(definition.id);
        if (!id) throw new Error(`Cannot translate legacy Ops logistic ${definition.id}`);
        const storage = canonicalStorageForLegacySchool(definition);
        return {
          ...definition,
          id,
          scope: storage.kind === 'locations'
            ? 'location' as const
            : storage.kind === 'squads'
              ? 'squad' as const
              : 'partner' as const,
          type: definition.id === 'school.campusPhoto' ? 'file_list' as const : definition.type,
          storage,
          ui: definition.ui
            ? {
                ...definition.ui,
                storagePathPrefix: storage.kind === 'locations'
                  ? 'locations/{locationId}/documents/opsUploads'
                  : 'partners/{partnerId}/documents/opsUploads',
              }
            : undefined,
        };
      }),
  );

export const LEGACY_SCHOOL_LOGISTIC_ID_MAP: Readonly<Record<string, string>> =
  Object.freeze(
    OPS_LOGISTICS_CATALOG
      .filter((definition) => definition.id.startsWith('school.'))
      .reduce<Record<string, string>>((result, definition) => {
        const canonical = canonicalLogisticIdForLegacySchool(definition.id);
        if (canonical) result[definition.id] = canonical;
        return result;
      }, {}),
  );
